import React from 'react';
import { Button, Card, Form, Grid, Input, InputNumber, Typography } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DoubleRightOutlined,
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

const ChooseResponseFlow = ({ app, loadingApp, onDirectAccept, onRejectRequest }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.sm;
  const [form] = Form.useForm();
  const [showRejectReason, setShowRejectReason] = React.useState(false);
  const bidAmount = Form.useWatch('bidAmount', form);
  const highestBid = Number(app.highestBid) || 0;
  const minimumBid = highestBid > 0 ? highestBid + 100 : 1;
  const canAccept = bidAmount !== undefined && bidAmount !== null && bidAmount !== '' && Number(bidAmount) >= minimumBid;
  const acceptDisabled = !canAccept || loadingApp === app.id;

  const handleReject = async () => {
    if (!showRejectReason) {
      form.setFields([{ name: 'bidAmount', errors: [] }]);
      setShowRejectReason(true);
      return;
    }

    const reason = form.getFieldValue('reason');
    if (!reason || !reason.trim()) {
      form.setFields([
        {
          name: 'reason',
          errors: ['Please enter reject reason'],
        },
      ]);
      return;
    }

    onRejectRequest(app.id, reason.trim());
  };

  return (
    <div style={{ animation: 'fadeIn 0.2s ease-out' }}>
      <Title level={4} style={{ marginTop: 0, marginRight: 0, marginBottom: '8px', marginLeft: 0, color: '#096dd9' }}>
        <DoubleRightOutlined style={{ marginRight: '8px' }} />
        Respond to Request
      </Title>

      <Paragraph style={{ color: '#595959', display: 'block', marginBottom: '24px', fontSize: '14px' }}>
        Choose whether to accept this inspection request or reject it. The request will be removed from Quick Actions after your response.
      </Paragraph>

      {highestBid > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #fff1b8 0%, #fff7e6 100%)',
          border: '2px solid #faad14',
          borderRadius: '12px',
          boxShadow: '0 10px 24px rgba(250, 140, 22, 0.18)',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px 16px',
        }}>
          <Text strong style={{ color: '#8c4a00', fontSize: '16px', textTransform: 'uppercase' }}>
            Highest Bid
          </Text>
          <Text strong style={{ color: '#ad4e00', fontSize: '34px', lineHeight: 1, whiteSpace: 'nowrap' }}>
            ₹{highestBid.toLocaleString('en-IN')}
          </Text>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => onDirectAccept(app.id, values.bidAmount)}
        size="large"
      >
        {!showRejectReason && (
          <Form.Item
            label="Bid Amount"
            name="bidAmount"
            extra={(
              <>
                {highestBid > 0 && (
                  <div>Highest bid is ₹{highestBid.toLocaleString('en-IN')}. Enter at least ₹{minimumBid.toLocaleString('en-IN')}.</div>
                )}
                <div>This amount includes 5% commission.</div>
              </>
            )}
            rules={[
              { required: true, message: 'Please enter bid amount' },
              {
                validator: (_, value) => {
                  if (!value || Number(value) >= minimumBid) return Promise.resolve();
                  return Promise.reject(new Error(`Bid amount should be at least ₹${minimumBid.toLocaleString('en-IN')}`));
                },
              },
            ]}
            style={{ marginBottom: '16px' }}
          >
            <InputNumber
              prefix={<Text strong style={{ color: '#8c8c8c', marginRight: '4px' }}>₹</Text>}
              placeholder="Enter bid amount"
              style={{ width: '100%', borderRadius: '8px' }}
            />
          </Form.Item>
        )}

        {showRejectReason && (
          <Form.Item
            label="Reject Reason"
            name="reason"
            style={{ marginBottom: '16px' }}
          >
            <Input.TextArea
              autoFocus
              placeholder="Enter reject reason"
              rows={3}
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>
        )}

        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '16px', width: '100%' }}>
          {!showRejectReason && (
            <Button
              type="primary"
              htmlType="submit"
              icon={<CheckOutlined />}
              block
              size="large"
              disabled={acceptDisabled}
              loading={loadingApp === app.id}
              style={{
                height: '48px',
                borderRadius: '8px',
                fontWeight: 'bold',
                background: acceptDisabled ? '#f5f5f5' : '#52c41a',
                borderColor: acceptDisabled ? '#d9d9d9' : '#52c41a',
                color: acceptDisabled ? 'rgba(0, 0, 0, 0.25)' : '#fff',
              }}
            >
              Accept
            </Button>
          )}
          {showRejectReason && (
            <Button
              block
              size="large"
              onClick={() => {
                form.setFields([{ name: 'reason', errors: [] }]);
                setShowRejectReason(false);
              }}
              style={{ height: '48px', borderRadius: '8px', fontWeight: 'bold' }}
            >
              Cancel
            </Button>
          )}
          <Button
            danger
            icon={<CloseOutlined />}
            block
            size="large"
            loading={loadingApp === app.id}
            onClick={handleReject}
            style={{ height: '48px', borderRadius: '8px', fontWeight: 'bold' }}
          >
            {showRejectReason ? 'Confirm Reject' : 'Reject'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

const QuoteActionPanel = ({
  app,
  loadingApp,
  onRejectRequest,
  onDirectAccept,
}) => {
  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  return (
    <Card
      variant="borderless"
      style={{
        borderRadius: '12px',
        background: 'linear-gradient(145deg, #f0f5ff 0%, #e6f0ff 100%)',
        border: '1px solid #adc6ff',
        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.08)'
      }}
      styles={{ body: { padding: isMobile ? '20px 16px' : '32px 24px' } }}
    >
      <div>
        <ChooseResponseFlow
          app={app}
          loadingApp={loadingApp}
          onDirectAccept={onDirectAccept}
          onRejectRequest={onRejectRequest}
        />
      </div>
    </Card>
  );
};

export default QuoteActionPanel;
